import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import {
  AnimatedSpinner,
  Container,
  Flex,
  Grid,
  TabPanel,
  TabPanels,
  Tabs,
} from '@tq/ui-components';

import { useCaseDetails, useConsultation } from 'api';
import { MobileTabList } from 'components';
import { useIsMobile } from '@tq/ui-components/dist/hooks';
import { isRpc, ToDisplayFeatureFlagged } from 'tools';

import {
  ApplicationDetails,
  CaseDetailsSummary,
  Comments,
  DesktopTabList,
  CaseDetailsHeader,
  Documents,
  MapDetails,
  Plans,
  RelatedCases,
} from './components';
import { HttpError } from '../../types';
import { useNavigate } from '../../hooks';
import { useCaseDetailsFeatures } from './hooks/useCaseDetailsFeatures';

export const CaseDetails: React.FC = () => {
  const { id } = useParams();
  const { data: consultation, error, isError } = useConsultation(+(id ?? 0));
  const {
    data: caseDetails,
    isLoading,
    isLoadingError,
    isRefetchError,
  } = useCaseDetails(+(id ?? 0));
  const { navigateToErrorPage } = useNavigate();
  const rpc = isRpc(consultation);
  const isMobile = useIsMobile();
  const tabLabels = useCaseDetailsFeatures(rpc);
  const [tabIndex, setTabIndex] = useState(0);
  const tabPanelStyle = {
    border: { base: 'none', lg: `1px solid ${COLOURS.gray[400]}` },
    px: { base: 0, lg: 5 },
    py: { base: 0, lg: 4 },
  };
  const showSpinner = isLoading || isLoadingError || isRefetchError;

  useEffect(() => {
    if (isError && error) {
      navigateToErrorPage(error as HttpError);
    }
  }, [isError, error, navigateToErrorPage]);

  return showSpinner ? (
    <Flex justifyContent='center'>
      <AnimatedSpinner size='lg' />
    </Flex>
  ) : (
    <Container px={{ base: 0, lg: 4, xl: 0 }}>
      <Grid gridGap={8} mb={12}>
        <CaseDetailsHeader consultation={consultation} />
        <CaseDetailsSummary
          consultation={consultation}
          caseDetails={caseDetails}
          isRpc={rpc}
        />
        <Tabs
          index={isMobile ? tabIndex : undefined}
          variant={isMobile ? 'line' : 'enclosed'}
          p={0}
          mb={12}
          mx={{ base: -5, lg: 0 }}
          isManual={true}
        >
          {isMobile ? (
            <MobileTabList
              activeTabIndex={tabIndex}
              onChangeActiveTab={setTabIndex}
              tabLabels={tabLabels}
            />
          ) : (
            <DesktopTabList tabLabels={tabLabels} caseDetails={caseDetails} isRpc={rpc} />
          )}
          <TabPanels
            my={{ base: 5, lg: 0 }}
            border={{ base: 0, md: 'unset' }}
            p={{ base: 0, md: 'unset' }}
            minH={{ base: undefined, md: 400 }}
            color={COLOURS.black}
            overflow={{ base: 'scroll', lg: 'auto' }}
          >
            {!rpc && (
              <TabPanel {...tabPanelStyle}>
                <ApplicationDetails
                  consultation={consultation}
                  application={caseDetails?.application}
                  dataItemsToDisplay={caseDetails?.dataItemsToDisplay}
                />
              </TabPanel>
            )}
            <TabPanel {...tabPanelStyle}>
              <MapDetails
                applicationReference={consultation?.reference}
                consultation={consultation}
                caseDetails={caseDetails}
                isRpc={rpc}
              />
            </TabPanel>
            {ToDisplayFeatureFlagged('disable_related_consultee_cases', true) && !rpc && (
              <TabPanel {...tabPanelStyle}>
                <RelatedCases caseDetails={caseDetails} />
              </TabPanel>
            )}
            <TabPanel {...tabPanelStyle}>
              <Documents
                consultation={consultation}
                documents={caseDetails?.application?.documents}
              />
            </TabPanel>
            {!rpc && (
              <TabPanel {...tabPanelStyle}>
                <Plans consultation={consultation} plans={caseDetails?.application?.plans} />
              </TabPanel>
            )}
            {ToDisplayFeatureFlagged('disable_consultee_comments', true) && !rpc && (
              <TabPanel {...tabPanelStyle}>
                <Comments
                  comments={caseDetails?.application?.comments}
                  consultation={consultation}
                />
              </TabPanel>
            )}
          </TabPanels>
        </Tabs>
      </Grid>
    </Container>
  );
};
